import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state/';
import { upperSnakeCase } from '@/utils';
import { BLISTER_HEIGHT_LABEL, BLISTER_TYPE, PIT_DEPTH_LABEL } from '@/constants';
import { useMemo } from 'react';

type BlisterFieldKey =
  | 'estimatedHeight'
  | 'manualSetHeight'
  | 'blisterHeightIndex'
  | 'maxHeight'
  | 'wallLoss'
  | 'residualThickness'
  | 'residualCorrosionAllowance'
  | 'pdrHeight'
  | 'verifiedMaxHeight'
  | 'verifiedResidualThickness'
  | 'verifiedResidualCorrosionAllowance'
  | 'measurementConfidence'
  | 'distanceConfidence'
  | 'heightType';

type BlisterFieldNames = Record<BlisterFieldKey, string>;

type BlisterFieldConfigResult = Record<
  BlisterFieldKey,
  {
    name: string;
    hidden: boolean;
  }
>;

type BlisterFieldConfig = {
  name?: string | null;
  hidden?: boolean | null;
};

const DEFAULT_BLISTER_FIELD_NAMES: BlisterFieldNames = {
  estimatedHeight: 'Estimated Height',
  manualSetHeight: 'Manual Set Height',
  blisterHeightIndex: 'Blister Height Index',
  maxHeight: 'Blister Height',
  wallLoss: 'Estimated Wall Loss',
  residualThickness: 'Residual Thickness',
  residualCorrosionAllowance: 'Residual Corrosion Allowance',
  pdrHeight: 'PDR Height',
  heightType: 'Calculation Method',
  verifiedMaxHeight: 'Verified Height',
  verifiedResidualThickness: 'Verified Residual Thickness',
  verifiedResidualCorrosionAllowance: 'Verified Residual Corrosion Allowance',
  measurementConfidence: 'Measurement Confidence',
  distanceConfidence: 'Distance Confidence',
};

export const useBlisterFieldConfig = ({
  type,
}: {
  type: string;
}): Partial<BlisterFieldConfigResult> => {
  const allPoiTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const fieldConfig = useMemo(() => {
    const upperType = upperSnakeCase(type);
    const selectedPoiTemplate = allPoiTemplates.find(
      (template) =>
        upperSnakeCase(template.key) === upperType ||
        upperSnakeCase(template.name) === upperType
    );

    const blisterFieldsConfig = selectedPoiTemplate?.blisterFieldsConfig;

    const mergedFieldConfig: Partial<BlisterFieldConfigResult> = {};

    // Initialize with default values
    (Object.keys(DEFAULT_BLISTER_FIELD_NAMES) as BlisterFieldKey[]).forEach((fieldKey) => {
      mergedFieldConfig[fieldKey] = {
        name: DEFAULT_BLISTER_FIELD_NAMES[fieldKey],
        hidden: false,
      };
    });

    // Override maxHeight based on type
    if (mergedFieldConfig.maxHeight) {
      mergedFieldConfig.maxHeight.name =
        type === BLISTER_TYPE ? BLISTER_HEIGHT_LABEL : PIT_DEPTH_LABEL;
    }

    // Apply template-specific configuration
    if (selectedPoiTemplate && blisterFieldsConfig) {
      (Object.keys(blisterFieldsConfig) as BlisterFieldKey[]).forEach((fieldKey) => {
        const fieldConfigItem = (
          blisterFieldsConfig as Record<string, BlisterFieldConfig | undefined>
        )[fieldKey];

        if (fieldConfigItem && fieldKey in DEFAULT_BLISTER_FIELD_NAMES) {
          if (fieldConfigItem.name) {
            mergedFieldConfig[fieldKey]!.name = fieldConfigItem.name;
          }
          if (fieldConfigItem.hidden !== undefined && fieldConfigItem.hidden !== null) {
            mergedFieldConfig[fieldKey]!.hidden = fieldConfigItem.hidden;
          }
        }
      });
    }

    return mergedFieldConfig;
  }, [allPoiTemplates, type]);

  return fieldConfig;
};
